import { useEffect } from 'react';

import { clsx } from 'clsx';

import vatStyles from './VATReturnsSwitch.module.css';
import { useGetDocuments } from '../../../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../../../hooks/useContextStateKey';
import { useFileUploadReducer } from '../../../../../hooks/useFileUploadReducer';
import { TOfferDataRequirement } from '../../../../../types';
import styles from '../../../DocumentRequirement/DocumentRequirement.module.css';
import Required from '../../../DocumentRequirement/Required';
import UploadButton from '../../../UploadButton/UploadButton';
import UploadedFiles from '../../../UploadedFiles/UploadedFiles';
import { TVatUploadStatus } from '../../TradingHistoryUK';

type TProps = {
  quantifier: number;
  isSubmitted: boolean;
  isOptional?: boolean;
  disabled?: boolean;
  status?: TOfferDataRequirement['actions'][number]['status'];
  setUploadStatus: (status: TVatUploadStatus) => void;
  uploadStatus: TVatUploadStatus;
};
const VATReturns = ({
  quantifier,
  isSubmitted,
  isOptional,
  disabled,
  status,
  setUploadStatus,
  uploadStatus,
}: TProps) => {
  const stateKey = useContextStateKey();
  const { documents } = useGetDocuments(stateKey);

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  useEffect(() => {
    if (uploadStatus === 'required') {
      if (hasUploadedSuccessfully()) {
        setUploadStatus('pending');
      }
    }
  }, [uploadedFiles]);

  useEffect(() => {
    if (documents) {
      const documentsForThisDocumentType = documents.data.filter(
        (document) => document.attributes.document_type === 'vat return',
      );
      setAllFiles(documentsForThisDocumentType);
    }
  }, [documents]);

  const hasUploadedSuccessfully = () => {
    const successfulUploads = uploadedFiles.filter(
      (file) => file.status === 'success',
    );

    return successfulUploads.length > 0;
  };

  const renderRequiredError = () => {
    if (
      !disabled &&
      isSubmitted &&
      (uploadStatus === 'required' ||
        status === 'required' ||
        status === 'error') &&
      !hasUploadedSuccessfully()
    ) {
      return <Required />;
    }
  };

  const quarter = quantifier / 3;
  const title =
    quarter > 1
      ? `Business VAT returns for the last ${quarter} quarters`
      : 'Business VAT returns for the last quarter';

  return (
    <div
      id="document-requirement-vat return"
      className={clsx({
        [styles.optionalDocumentContainer]: isOptional,
        [styles.requiredDocumentContainer]: !isOptional,
      })}
    >
      <div className={styles.documentRequirement}>
        <div
          className={clsx(
            vatStyles.vatDescription,
            styles.descriptionContainer,
            {
              [vatStyles.hideVAT]: disabled,
              [vatStyles.showVAT]: !disabled,
            },
          )}
          data-testid="vat-return"
        >
          <h3
            className={isOptional ? styles.optionalTitle : styles.requiredTitle}
          >
            {title}
            {renderRequiredError()}
          </h3>

          <div className={styles.description}>
            <ul>
              <li>
                We accept PDF statements downloaded from either{' '}
                <a
                  href="https://www.tax.service.gov.uk/gg/sign-in?continue=/business-account/vat"
                  target="_blank"
                  rel="noreferrer"
                >
                  the HMRC portal
                </a>{' '}
                or from your accounting software
              </li>
              <li>
                Need help? Check out our VAT return guide{' '}
                <a
                  href="https://static.iwoca.com/pdf/vat_return_instructions.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
              </li>
            </ul>
          </div>
          {uploadedFiles && (
            <UploadedFiles files={uploadedFiles} removeFile={removeFile} />
          )}
        </div>
        <div className={styles.buttonContainer}>
          <UploadButton
            documentType="vat return"
            setFileLoading={setFileLoading}
            setFileSuccess={setFileSuccess}
            setFileError={setFileError}
            setFileReject={setFileReject}
            isOptional={isOptional}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default VATReturns;
